import { useCallback, useMemo, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import LineRefreshIcon from 'LuxKit/Icons/line/LineRefreshIcon'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import IconButton from 'LuxKit/Button/IconButton'
import Group from 'components/utils/Group'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { mapRatingBreakdown } from 'lib/reviews/ratingBreakdownUtils'
import useUserReviewsSummary from 'hooks/Reviews/useUserReviewsSummary'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchCustomerReviewSummary } from 'actions/ReviewActions'
import { FLASH_DESKTOP_HEADER_SUMMARY_CHARACTER_LIMIT } from '../useFlashDesktopOfferHeaderExperiment'

const SUMMARY_CARD_BLOCK_SIZE = 136

const SummaryCard = styled.div`
  box-sizing: border-box;
  block-size: ${rem(SUMMARY_CARD_BLOCK_SIZE)};
  overflow: hidden;
  padding: ${rem(16)};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const SummaryBody = styled(BodyText)`
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  line-clamp: 4;
`

const CategoryScore = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${rem(32)};
  height: ${rem(24)};
  padding-block: 0;
  padding-inline: ${rem(6)};
  border-radius: ${props => props.theme.borderRadius.S};
  background: ${props => props.theme.palette.messaging.success.lightBackground};
`

const SUMMARY_CHARACTER_LIMIT = FLASH_DESKTOP_HEADER_SUMMARY_CHARACTER_LIMIT
const HEADER_REVIEW_SUMMARY_OPTIONS = { maxCharacters: SUMMARY_CHARACTER_LIMIT }
const DISABLED_HEADER_REVIEW_SUMMARY_OPTIONS = { enabled: false }
const SUMMARY_ELLIPSIS = '…'
const REVIEW_SUMMARY_REFRESH_EMAILS = new Set([
  'luxing.li@luxuryescapes.com',
  'lukas.zimmermann@luxuryescapes.com',
  'nick.griffith@luxuryescapes.com',
])

function limitReviewSummary(summary: string) {
  const characters = Array.from(summary)
  if (characters.length <= SUMMARY_CHARACTER_LIMIT) {
    return summary
  }

  const budget = SUMMARY_CHARACTER_LIMIT - Array.from(SUMMARY_ELLIPSIS).length
  const clipped = characters.slice(0, budget).join('')
  const lastSpace = clipped.lastIndexOf(' ')
  const wordBounded = lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped

  return `${wordBounded.trimEnd()}${SUMMARY_ELLIPSIS}`
}

interface Props {
  offer: App.HotelOffer
}

function FlashDesktopOfferHeaderReviews(props: Props) {
  const { offer } = props
  const intl = useIntl()
  const rating = offer.property.rating
  const authoredSummary = offer.property.aiReviewSummaryOverride?.trim() || undefined
  const [reviewSummary, fetchingReviewSummary] = useUserReviewsSummary(
    offer.property.id,
    'luxuryescapes',
    authoredSummary ? DISABLED_HEADER_REVIEW_SUMMARY_OPTIONS : HEADER_REVIEW_SUMMARY_OPTIONS,
  )
  const summary = authoredSummary ?? reviewSummary?.summary
  const limitedSummary = useMemo(() => summary ? limitReviewSummary(summary) : undefined, [summary])
  const isSummaryLoading = !authoredSummary && fetchingReviewSummary
  const dispatch = useAppDispatch()
  const accountEmail = useAppSelector(state => state.auth.account.email)
  const canRefreshReviewSummary = REVIEW_SUMMARY_REFRESH_EMAILS.has(accountEmail?.toLowerCase() ?? '')
  const refreshReviewSummaryLabel = intl.formatMessage({
    id: 'cltQxU9kIfOc8HgG-GyH',
    defaultMessage: 'Refresh review summary',
    description: 'Button for selected staff accounts to regenerate the desktop flash offer header AI review summary',
  })
  const handleRefreshReviewSummary = useCallback(() => {
    dispatch(fetchCustomerReviewSummary(
      offer.property.id,
      'luxuryescapes',
      undefined,
      true,
      SUMMARY_CHARACTER_LIMIT,
      true,
    ))
  }, [dispatch, offer.property.id])
  const reviewsLink = useCallback((chunks: ReactNode) => (
    <TextLink size="small" weight="bold" underline href="#customer-reviews">{chunks}</TextLink>
  ), [])

  const highestCategory = useMemo(() => {
    return mapRatingBreakdown(rating?.reviewsRatingBreakdown, intl)
      .filter(category => category.score > 0)
      .reduce<{ score: number; label: string } | undefined>((highest, category) => {
        if (!highest || category.score > highest.score) {
          return category
        }
        return highest
      }, undefined)
  }, [intl, rating?.reviewsRatingBreakdown])
  const highestCategoryScoreLabel = highestCategory
    ? intl.formatNumber(highestCategory.score, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    : undefined

  return (
    <Group direction="vertical" gap={8}>
      <Group direction="horizontal" gap={12} wrap="wrap" verticalAlign="center">
        {isOfferRatingDisplayable(rating) && (
          <OfferRating link inlineLabel variant="flat" rating={rating} />
        )}
        {highestCategory && highestCategoryScoreLabel && (
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <CategoryScore>
              <Caption variant="large" weight="bold" colour="success-light-contrast">{highestCategoryScoreLabel}</Caption>
            </CategoryScore>
            <BodyText variant="small" weight="bold">{highestCategory.label}</BodyText>
          </Group>
        )}
      </Group>
      {limitedSummary && (
        <SummaryCard>
          <Group direction="vertical" gap={8}>
            <SummaryBody variant="medium" colour="light-foreground-alt">{limitedSummary}</SummaryBody>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <SolidSparklesIcon size="XS" />
              <Caption variant="small" colour="light-foreground-alt">
                <FormattedMessage
                  id="kPeNxwZKdDJ-Kpk1wz0s"
                  defaultMessage="AI-SUMMARY · Based on <link>review highlights</link>"
                  description="Caption under the desktop flash offer header AI review summary, linking to the reviews section"
                  values={{ link: reviewsLink }}
                />
              </Caption>
              {canRefreshReviewSummary && (
                <IconButton
                  kind="tertiary"
                  variant="ghost"
                  size="xsmall"
                  aria-label={refreshReviewSummaryLabel}
                  onClick={handleRefreshReviewSummary}
                >
                  <LineRefreshIcon colour="neutral-one" />
                </IconButton>
              )}
            </Group>
          </Group>
        </SummaryCard>
      )}
      {isSummaryLoading && <SummaryCard aria-busy="true" />}
    </Group>
  )
}

export default FlashDesktopOfferHeaderReviews
